import { ProDescriptions } from "@ant-design/pro-components";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, message } from "antd";
import { useSize } from "ahooks";
import { usePolling } from "@/hooks";
import {
  BasicTable,
  RightDrawer,
  AlarmsHeader,
  AlarmsReport,
} from "@/components";
import { useMemo } from "react";
import { TABLEPAI, DEPLOYMENT } from "@/enums/pageEnum";
import { getTimeToString, getLiftNumberNA, getIsNumberNA } from "@/utils/toolt";
import { GetAlarmStatus } from "@/utils/commonConfig";
import { setAcknowledge, liftClosed } from "@api";

export default function LiftFaultDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, loading, fetchData } = usePolling({
    url: `/api/lift/fault/${id}`,
  });
  const size = useSize(document.querySelector(".ant-drawer"));

  const tableHeight = useMemo(() => {
    const currentHeight = size?.height ? size.height - 824 : 0;
    return currentHeight < 150 ? 150 : currentHeight;
  }, [size]);

  const columns = useMemo(
    () => [
      {
        title: t("S/N"),
        dataIndex: "id",
        width: 50,
        renderText: (_: string, __: string, index: number) => `${index + 1}`,
      },
      { title: t("complainantName"), dataIndex: ["createUser", "nickname"] },
      {
        title: t("complaintDate"),
        dataIndex: "createdAt",
        renderText: (_: number) => getTimeToString(_),
      },
      { title: t("description"), dataIndex: "description" },
      { title: t("complainant"), dataIndex: "complainant" },
      { title: t("address"), dataIndex: "buildingAddress" },
      { title: t("phoneNumber"), dataIndex: "phone" },
    ],
    [t]
  );

  const getReport = useMemo(() => {
    return (
      <AlarmsReport
        data={data}
        fieldsKeys={{
          name: "technicianName",
          code: "faultCode",
          result: "result",
        }}
        request={liftClosed}
        onSubmit={() => {
          fetchData();
        }}
      />
    );
  }, [
    data?.operationalStatus,
    data?.reportAt,
    data?.acknowledgeAt,
    data?.arrivalTs,
  ]);

  return (
    <RightDrawer title={data?.liftName} loading={loading}>
      <AlarmsHeader data={data} />
      <ProDescriptions
        dataSource={data}
        className="faultInputCode"
        emptyText=" "
        columns={[
          {
            title: t("liftFaultNumber"),
            dataIndex: "alarmLiftFaultNumber",
            style: { width: 300 },
          },
          {
            title: t("setTime"),
            dataIndex: "alarmStartTs",
            render: getLiftNumberNA,
          },
          {
            title: "",
            dataIndex: "address",
            className: "acknowledgeCalss",
            children: (
              <Button
                variant="outlined"
                color="primary"
                disabled={data?.operationalStatus !== DEPLOYMENT}
                onClick={() => {
                  setAcknowledge(id).then(() => {
                    message.success(t("success"));
                    fetchData();
                  });
                }}
              >
                {t("acknowledge")}
              </Button>
            ),
          },
          GetAlarmStatus(t),
          {
            title: t("clearTime"),
            dataIndex: "alarmClearTs",
            render: getLiftNumberNA,
          },
          {
            title: t("duration"),
            dataIndex: "duration",
            render: getIsNumberNA,
          },
        ]}
      />
      <div className="flex items-center justify-start py-4 border-t border-[#E8E9ED] dark:border-[#ffffff21] mt-6">
        <div className="w-[300px]">
          {t("complaintCount")}: {data?.complaintCnt}
        </div>
        <div>
          {t("fristCom")}: {getTimeToString(data?.firstComplaintAt)}
        </div>
      </div>
      {data && id && (
        <BasicTable
          isShowCheckbox={false}
          columns={columns}
          isDefaultToolBar={false}
          isPagination={false}
          longPolling
          tableHeight={tableHeight}
          isAddPathParams={false}
          className="min-h-max !p-0 mb-5 liftFaultTable"
          requestUrl={`${TABLEPAI.LIFT_COMPLAINT}?liftFaultId=${id}`}
        />
      )}
      {getReport}
    </RightDrawer>
  );
}
